import { G } from '@mobily/ts-belt';
import { match, P } from 'ts-pattern';

import { generatePlanAllocationChoiceId } from '@Common/bo/b2b_campaign_plan_options';
import { B2bSavingPlanDeductibility, B2bSavingPlanType } from '@Common/types/b2b/plans';
import { B2bCashSource } from '@Common/types/bo/b2b/b2b';
import { B2bCampaignType } from '@Common/types/bo/b2b/campaigns';
import { B2bCampaignActionHandler, B2bContract } from '@Common/types/bo/b2b/contracts';
import { getContractTypeDefinition } from '@Common/types/bo/b2b/contracts/contract_type_definitions';
import { MatchingContributionConfig } from '@Common/types/bo/b2b/matching_contribution_config';
import { GetB2bCompaniesPlansEndpoint } from '@Common/types/bo/b2b/plans';

export function getCampaignProcessingOptionsFromType({
  type,
  contract,
  isMatchingContributionComputedByNullOnAllPlans,
  mcEnabled,
}: {
  type: B2bCampaignType;
  contract: Omit<B2bContract, 'dist_correspondent_email_state'>;
  isMatchingContributionComputedByNullOnAllPlans?: boolean;
  mcEnabled: boolean;
}): {
  allocation_computed_by: B2bCampaignActionHandler;
  choice_collected_by: B2bCampaignActionHandler;
  payout_by: B2bCampaignActionHandler | null;
  matching_contribution_computed_by: B2bCampaignActionHandler | null;
} {
  if (isMaturityCampaignType(type)) {
    return {
      allocation_computed_by: 'company',
      choice_collected_by: 'distributor',
      payout_by: 'distributor',
      matching_contribution_computed_by: 'distributor',
    };
  }

  if (type === 'matching_contribution') {
    return {
      allocation_computed_by: 'company',
      choice_collected_by: 'company',
      payout_by: 'company',
      matching_contribution_computed_by: 'company',
    };
  }

  const defaultHandler = getDefaultCampaignHandler(contract);
  const allocationComputedBy = contract.campaigns[type]?.allocation_computed_by ?? 'company';
  const choiceCollectedBy = contract.campaigns[type]?.choice_collected_by ?? defaultHandler;
  const payoutBy = contract.campaigns[type]?.payout_by ?? defaultHandler;
  const matchingContributionComputedBy =
    contract.campaigns[type]?.matching_contribution_computed_by ?? 'company';

  const allocation_computed_by =
    type === 'value_sharing_bonus' ||
    type === 'voluntary_deposit' ||
    type === 'unused_vacation_days'
      ? 'company'
      : allocationComputedBy;
  const choice_collected_by = type === 'voluntary_deposit' ? 'company' : choiceCollectedBy;
  const payout_by = match(type)
    .returnType<B2bCampaignActionHandler | null>()
    .with('unused_vacation_days', () => null)
    .with('value_sharing_bonus', () => 'company')
    .with('participation', 'profit_sharing', 'voluntary_deposit', P.nullish, () => payoutBy)
    .exhaustive();

  const shouldUseNullMc = !mcEnabled || isMatchingContributionComputedByNullOnAllPlans;

  return {
    allocation_computed_by,
    choice_collected_by,
    payout_by,
    matching_contribution_computed_by: shouldUseNullMc ? null : matchingContributionComputedBy,
  };
}

export function isCashSourceSupportedByMatchingContributionConfig(
  config: MatchingContributionConfig | null | undefined,
  cashSource: B2bCashSource,
): boolean {
  if (G.isNullable(config)) {
    return false;
  }

  return match(config)
    .with({ type: 'simple' }, { type: 'company_simple' }, (cfg) =>
      G.isNotNullable(cfg.limits_per_contribution_type[cashSource]),
    )
    .with({ type: 'csp_code' }, { type: 'limit_brackets' }, { type: 'seniority_brackets' }, (cfg) =>
      cfg.supported_contribution_types.includes(cashSource),
    )
    .with(
      { type: 'csp_code_per_cash_source' },
      { type: 'limit_brackets_per_cash_source' },
      { type: 'csp_code_per_cash_source_brackets' },
      (cfg) => cfg.supported_contribution_types.some((entry) => entry.cash_source === cashSource),
    )
    .exhaustive();
}

function getDefaultCampaignHandler(
  contract: Pick<B2bContract, 'contract_type'>,
): B2bCampaignActionHandler {
  return getContractTypeDefinition(contract.contract_type).isExternal ? 'distributor' : 'company';
}

export const generateAllocationChoiceId = ({
  id,
  planType,
  deductibility,
  planId,
  isChoiceConcernCcb,
}: {
  id: 'payout' | undefined;
  planType: B2bSavingPlanType | null | undefined;
  deductibility: B2bSavingPlanDeductibility | null | undefined;
  planId: number | 'payout' | 'ccb' | null | undefined;
  isChoiceConcernCcb: boolean;
}) => {
  if (isChoiceConcernCcb) {
    return `ccb_${planId}`;
  }

  return planType && typeof planId === 'number'
    ? generatePlanAllocationChoiceId(planId, deductibility)
    : id;
};

export const generatePlanTypeLabeled = ({
  planType,
  planLabel,
}: {
  planType: B2bSavingPlanType | null | undefined;
  planLabel: string | null | undefined;
}): string => {
  if (G.isNullable(planType)) {
    return G.isNotNullable(planLabel) ? planLabel : 'undefined';
  }

  return planLabel ? `${planType} - (${planLabel})` : planType;
};

export const extractPlanLabelForAllocationChoice = ({
  plans,
  planId,
  planType,
}: {
  plans: GetB2bCompaniesPlansEndpoint['Client']['ResponseBody'];
  planId: number | 'payout' | 'ccb' | null | undefined;
  planType: B2bSavingPlanType | null | undefined;
}) => {
  return G.isNotNullable(planId) && G.isNotNullable(planType)
    ? (plans
        .find((plan) => plan.id.split('_').some((id) => id === planId.toString()))
        ?.shares_plans.find((sp) => sp.id === planId)?.plan_label ?? null)
    : null;
};

export function isMaturityCampaignType(
  type: B2bCampaignType,
): type is Extract<B2bCampaignType, 'ccb_matured_election' | 'withdrawal_at_maturity'> {
  return type === 'ccb_matured_election' || type === 'withdrawal_at_maturity';
}

export function doesCampaignTypeAcceptPayout(type?: B2bCampaignType | null): boolean {
  if (G.isNullable(type)) {
    return false;
  }
  const campaignTypeWithoutPayout: B2bCampaignType[] = [
    'voluntary_deposit',
    'unused_vacation_days',
    'matching_contribution',
  ];

  return !campaignTypeWithoutPayout.includes(type);
}
